import { useCallback, useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import { useAppSelector } from '~/redux/hooks'
import { selectShouldAutoValidateAnswer } from '~/bootcamp/slices/bootcamp-activity.slice'

import { BlockContent } from '../../types/activities.types'

import {
  CategorizationAnswerType,
  CategorizationActivityData,
  CategorizationProps,
  TaskStatus,
  CategorizationQuestion,
} from './types/Categorization.type'

import { sortData, generateCategorizationActivityData } from './types/Categorization.data'
import CategorizationApp from './app/CategorizationApp'

const emptyQuestion: CategorizationQuestion = {
  question: [{ children: [{ text: '' }] }],
  questionId: '',
  _key: '',
  category1: '',
  category1Title: '',
  category2: '',
  category2Title: '',
  elementsCategory1: [],
  elementsCategory2: [],
}

function Categorization({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onCurrentQuestionChange,
  actionButtonsFC,
  questions,
}: CategorizationProps): React.ReactElement {
  const [answer, setAnswer] = useState<boolean[]>([])
  const [question, setQuestion] = useState<BlockContent[]>([])
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [dataSorted, setDataSorted] = useState<boolean>(false)
  const [taskStatus, setTaskStatus] = useState<TaskStatus>(TaskStatus.Unchecked)
  const [visibleItem, setVisibleItem] = useState<string | null>(null)
  const [activityData, setActivityData] = useState<CategorizationActivityData>({
    question: emptyQuestion,
    questionIndex: 0,
  })
  const showOpenForCheat = useAppSelector(selectShouldAutoValidateAnswer)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEffect(() => {
    setAnswer([])
    setVisibleItem(null)
    setActivityData(generateCategorizationActivityData(questions, questionHistory))
  }, [questionHistory, questions])

  // Keyed on the questionId string: the effect above returns a fresh activityData object every run.
  // `visibleItem` rides the same writer as the identity so the key can never disagree.
  useEffect(() => {
    if (activityData.question.questionId) {
      onCurrentQuestionChange?.(activityData.question.questionId, visibleItem ? { visibleItem } : undefined)
    }
  }, [activityData.question.questionId, visibleItem, onCurrentQuestionChange])

  useEffect(() => {
    if (activityData.question.category1Title !== '' && activityData.question.category1Title !== '') {
      sortData(activityData.question)
      setDataSorted(true)
      setQuestion(activityData.question.question)
    }
  }, [activityData])

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    setTaskStatus(TaskStatus.Reset)
    setDataSorted(false)
    setActivityData({
      question: emptyQuestion,
      questionIndex: 0,
    })
    onNextQuestionClick()
  }

  function ClearForm(): void {
    setTaskStatus(TaskStatus.Reset)
    setAnswer([])
  }

  const showFeedbackMessage = useCallback(
    (responseFeedback: CategorizationAnswerType | null): void => {
      if (responseFeedback === null) return
      if (responseFeedback === CategorizationAnswerType.CORRECT || showOpenForCheat) {
        onAnswerStateChange(true, activityData.question.questionId)
      } else {
        onAnswerStateChange(false, activityData.question.questionId)
      }
    },
    [activityData.question.questionId, onAnswerStateChange, showOpenForCheat],
  )

  return (
    <ActivityWrapper>
      <div className="my-auto w-full justify-center items-center text-purple font-semibold text-md">
        {dataSorted && (
          <CategorizationApp
            onVisibleItemChange={setVisibleItem}
            question={question}
            setFeedBackMessage={showFeedbackMessage}
            setTaskStatus={setTaskStatus}
            taskStatus={taskStatus}
          />
        )}
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: taskStatus === TaskStatus.ReadyToCheck || taskStatus === TaskStatus.Wrong,
          correctAnswer: answerState ?? false,
          submitAnswerAction: () => {
            setTaskStatus(TaskStatus.Checking)
          },
          clearFormAction: () => {
            ClearForm()
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default Categorization
